/**
 * Insurance Connector Analytics Actions
 *
 * Mixpanel tracking actions for eligibility checks and insurance verification
 * outcomes, wired into the XState machine transitions.
 */

import { type EventContext, trackEvent } from "@frontend/lib/analytics";
import { differenceInYears, parse } from "date-fns";
import type { InsuranceOutcome } from "../actors/process-insurance.actor";
import type { InsuranceConnectorContext } from "../types";

function computeAge(birthday: string | undefined): number | undefined {
  if (!birthday) return undefined;
  const cleaned = birthday.replace(/\s/g, "").replace(/\//g, "-");
  const parsed = parse(cleaned, "MM-dd-yyyy", new Date());
  if (Number.isNaN(parsed.getTime())) return undefined;
  return differenceInYears(new Date(), parsed);
}

function buildBaseMetadata(ctx: InsuranceConnectorContext) {
  return {
    orgId: ctx.orgId,
    orgName: ctx.orgName,
    isReturnUser: !!ctx.isReturnUser,
    verified: !!ctx.verified,
    consumerId: ctx.consumerId ?? null,
  };
}

function buildInsuranceMetadata(ctx: InsuranceConnectorContext) {
  return {
    ...buildBaseMetadata(ctx),
    primaryPayer: ctx.primaryInsurance?.payer ?? null,
    primaryMemberId: ctx.primaryInsurance?.memberId ?? null,
    primaryInsuredState: ctx.primaryInsurance?.insuredState ?? null,
    hasSecondaryInsurance: !!ctx.secondaryInsurance,
    secondaryPayer: ctx.secondaryInsurance?.payer ?? null,
    secondaryMemberId: ctx.secondaryInsurance?.memberId ?? null,
    secondaryInsuredState: ctx.secondaryInsurance?.insuredState ?? null,
    isRetryFlow: !!ctx.isRetryFlow,
    verificationAttempts: ctx.verificationAttempts,
  };
}

function track(
  eventContext: EventContext,
  action: string,
  metadata: Record<string, unknown>,
) {
  trackEvent({
    category: "interaction",
    context: eventContext,
    action,
    metadata,
  });
}

// ---------------------------------------------------------------------------
// Eligibility events
// ---------------------------------------------------------------------------

export const trackEligibilityAgePassed = ({
  context,
}: { context: InsuranceConnectorContext }) => {
  track("eligibility", "eligibility_age_passed", {
    ...buildBaseMetadata(context),
    age: computeAge(context.birthday),
    consumerState: context.consumerState ?? null,
  });
};

export const trackEligibilityAgeFailed = ({
  context,
}: { context: InsuranceConnectorContext }) => {
  track("eligibility", "eligibility_age_failed", {
    ...buildBaseMetadata(context),
    age: computeAge(context.birthday),
    minimumAge: context.minimumAge ?? null,
    consumerState: context.consumerState ?? null,
  });
};

export const trackEligibilityStatePassed = ({
  context,
}: { context: InsuranceConnectorContext }) => {
  track("eligibility", "eligibility_state_passed", {
    ...buildBaseMetadata(context),
    consumerState: context.consumerState ?? null,
  });
};

export const trackEligibilityStateFailed = ({
  context,
}: { context: InsuranceConnectorContext }) => {
  track("eligibility", "eligibility_state_failed", {
    ...buildBaseMetadata(context),
    consumerState: context.consumerState ?? null,
    allowedStates: context.allowedStates ?? [],
  });
};

// ---------------------------------------------------------------------------
// Insurance verification response events
// ---------------------------------------------------------------------------

interface VerificationDoneEvent {
  type: string;
  output: {
    outcome: InsuranceOutcome;
    insuranceStates: Array<{
      status: string;
      network: string;
      hasBenefits: boolean;
      payer: string;
      memberId: string;
      state: string;
    }>;
    pendingPricesMode: boolean;
  };
}

function buildVerificationMetadata(
  ctx: InsuranceConnectorContext,
  event: VerificationDoneEvent,
) {
  const primary = event.output.insuranceStates?.[0];
  return {
    ...buildInsuranceMetadata(ctx),
    outcome: event.output.outcome,
    rawStatus: primary?.status ?? null,
    rawNetwork: primary?.network ?? null,
    rawHasBenefits: primary?.hasBenefits ?? null,
    pendingPricesMode: event.output.pendingPricesMode,
  };
}

export const trackInsuranceActive = ({
  context,
  event,
}: { context: InsuranceConnectorContext; event: VerificationDoneEvent }) => {
  track(
    "insurance",
    "insurance_verified_active",
    buildVerificationMetadata(context, event),
  );
};

export const trackInsuranceInactive = ({
  context,
  event,
}: { context: InsuranceConnectorContext; event: VerificationDoneEvent }) => {
  track(
    "insurance",
    "insurance_verified_inactive",
    buildVerificationMetadata(context, event),
  );
};

export const trackInsuranceOutOfNetwork = ({
  context,
  event,
}: { context: InsuranceConnectorContext; event: VerificationDoneEvent }) => {
  track(
    "insurance",
    "insurance_verified_out_of_network",
    buildVerificationMetadata(context, event),
  );
};

export const trackInsuranceUnverifiedFirst = ({
  context,
  event,
}: { context: InsuranceConnectorContext; event: VerificationDoneEvent }) => {
  track(
    "insurance",
    "insurance_verified_unverified",
    buildVerificationMetadata(context, event),
  );
};

export const trackInsuranceUnverifiedRetry = ({
  context,
  event,
}: { context: InsuranceConnectorContext; event: VerificationDoneEvent }) => {
  track(
    "insurance",
    "insurance_verified_unverified_retry",
    buildVerificationMetadata(context, event),
  );
};

export const trackInsuranceVerificationError = ({
  context,
  event,
}: {
  context: InsuranceConnectorContext;
  event: { type: string; error: unknown };
}) => {
  track("insurance", "insurance_verification_error", {
    ...buildInsuranceMetadata(context),
    outcome: "error",
    errorCode: (event.error as { code?: string })?.code ?? null,
    errorMessage: (event.error as Error)?.message ?? null,
  });
};
